import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { partnerDisplay, defaultMenu } from "../constants/partnerList";
import { jwtDecode } from "jwt-decode";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./header.scss";
import { fetchLogin, clearView } from "../../redux/slices/login";
import { useDispatch, useSelector } from "react-redux";

function useSecondaryMenu(partnerSiteId) {
  const partnerUid = partnerSiteId?.uid;
  return useMemo(() => {
    const matched = partnerDisplay.find((val) => val.id === partnerUid);
    return matched?.menu || defaultMenu;
  }, [partnerUid]);
}

function SecondaryMenu({ onMenuClick, isMobile = false }) {
  let partnerSiteId = null;
  const location = useLocation();
  const [openDropdownId, setOpenDropdownId] = useState(null);

  try {
    const raw = sessionStorage.getItem("partnerSiteId");
    partnerSiteId = raw ? JSON.parse(raw) : null;
  } catch {
    partnerSiteId = null;
  }

  const secondaryMenu = useSecondaryMenu(partnerSiteId);

  const toggleDropdown = (elementId) => {
    if (isMobile) {
      setOpenDropdownId(openDropdownId === elementId ? null : elementId);
    }
  };

  return (
    <ul className="navbar-nav me-auto">
      {secondaryMenu.map((element) => (
        <li className="nav-item dropdown" key={element.id}>
          <button
            className="nav-link dropdown-toggle"
            data-bs-toggle={isMobile ? "" : "dropdown"}
            aria-expanded={isMobile ? openDropdownId === element.id : false}
            aria-haspopup="true"
            aria-controls={`dropdown-${element.id}`}
            onClick={() => toggleDropdown(element.id)}
          >
            {element.name}
          </button>
          <ul
            className={`dropdown-menu ${
              isMobile && openDropdownId === element.id ? "show" : ""
            }`}
            id={`dropdown-${element.id}`}
            role="menu"
          >
            {element.subMenu.map((subelement) => (
              <li key={subelement.sid}>
                <Link
                  className="dropdown-item"
                  to={subelement.link}
                  role="menuitem"
                  onClick={(e) => {
                    if (location.pathname === subelement.link) {
                      e.preventDefault();
                      return;
                    }
                    onMenuClick(e);
                  }}
                >
                  {subelement.name}
                </Link>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

function MobileHeader({ onMenuClick, primaryMenu, handleSignOut }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const navCollapseRef = useRef(null);
  const userDropdownRef = useRef(null);
  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const toggleUserDropdown = () => setIsUserDropdownOpen((prev) => !prev);
  const location = useLocation();
  const navigate = useNavigate();
  const [cartItem, setCartItem] = useState(() => {
    try {
      const stored = sessionStorage.getItem("cart_selectedItem");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const { purchase, doPaypalPurchase, voucherPurchase } = useSelector(
    (state) => state.authentication
  );
  const checkoutResponse = purchase || doPaypalPurchase || voucherPurchase;
  const isCheckoutSuccess = !!checkoutResponse?.transactionId;
  const isBadCart = location.state?.isBadCart;

  const hasCartItem = cartItem != null;

  useEffect(() => {
    if (isCheckoutSuccess || isBadCart) {
      sessionStorage.removeItem("cart_selectedItem");
      setCartItem(null);
      return;
    }

    try {
      const stored = sessionStorage.getItem("cart_selectedItem");
      const effectiveItem =
        location.state?.selectedItem || (stored ? JSON.parse(stored) : null);

      if (effectiveItem) {
        sessionStorage.setItem(
          "cart_selectedItem",
          JSON.stringify(effectiveItem)
        );
        setCartItem(effectiveItem);
      } else {
        sessionStorage.removeItem("cart_selectedItem");
        setCartItem(null);
      }
    } catch {
      const fallbackItem = location.state?.selectedItem || null;
      if (fallbackItem) {
        setCartItem(fallbackItem);
      } else {
        setCartItem(null);
      }
    }
  }, [
    location.state?.selectedItem,
    location.state?.isBadCart,
    location.pathname,
    isCheckoutSuccess,
    isBadCart,
  ]);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    navCollapseRef.current?.classList.remove("show");
    onMenuClick?.();
  }, [onMenuClick]);

  const closeUserDropdown = () => {
    setIsUserDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        navCollapseRef.current &&
        !navCollapseRef.current.contains(e.target)
      ) {
        closeMenu();
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(e.target)
      ) {
        closeUserDropdown();
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [closeMenu]);

  return (
    <div className="container d-md-none d-block bg-white mobile-nav">
      <nav className="navbar navbar-expand-md fixed-top bg-light py-3 px-3">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <div>
            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#nav_dropdown_collapse"
              aria-controls="nav_dropdown_collapse"
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation"
              onClick={toggleMenu}
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <div
              id="nav_dropdown_collapse"
              ref={navCollapseRef}
              className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`}
            >
              <SecondaryMenu onMenuClick={closeMenu} isMobile={true} />
            </div>
          </div>

          <Link
            to="/vehiclehistory/"
            onClick={(e) =>
              location.pathname === "/vehiclehistory/" && e.preventDefault()
            }
            className="mx-auto"
          >
            <img
              src={require("../../assets/images/autocheckLogoM.png")}
              alt="AutoCheck by Experian"
              className="img-fluid"
            />
          </Link>

          <div className="d-flex align-items-center">
            <div
              className="position-relative d-inline-block"
              ref={userDropdownRef}
            >
              <button
                className="btn btn-light icos"
                type="button"
                onClick={toggleUserDropdown}
                aria-label="Account"
                aria-expanded={isUserDropdownOpen}
              >
                <i className="bi bi-person-fill"></i>
              </button>
              <ul
                className={`dropdown-menu utilitymenu ${
                  isUserDropdownOpen ? "show" : ""
                }`}
                role="menu"
              >
                {primaryMenu.map((element) => (
                  <li key={element.id}>
                    {element.onClick ? (
                      <button
                        className="dropdown-item"
                        onClick={(e) => {
                          element.onClick(e);
                          closeUserDropdown();
                        }}
                        role="menuitem"
                      >
                        {element.name}
                      </button>
                    ) : (
                      <Link
                        className="dropdown-item"
                        to={element.link}
                        target={element.target || ""}
                        role="menuitem"
                        onClick={(e) => {
                          if (location.pathname === element.link) {
                            e.preventDefault();
                          }
                          closeUserDropdown();
                        }}
                      >
                        {element.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <button
              className={`btn icos ms-2 ${
                hasCartItem ? "btn-blue" : "btn-light"
              }`}
              aria-label="Cart"
              disabled={!hasCartItem}
              style={{
                opacity: hasCartItem ? 1 : 0.5,
                cursor: hasCartItem ? "pointer" : "not-allowed",
              }}
              onClick={(e) => {
                e.preventDefault();
                if (hasCartItem) {
                  if (localStorage.getItem("consumerSite_token")) {
                    navigate("/vehiclehistory/checkout", {
                      state: { selectedItem: cartItem },
                    });
                  } else {
                    navigate("/vehiclehistory/login", {
                      state: { selectedItem: cartItem },
                    });
                  }
                }
              }}
            >
              <i className="bi bi-cart-fill"></i>
            </button>
          </div>
        </div>
      </nav>
    </div>
  );
}

function DesktopHeader({ primaryMenu, location, onMenuClick }) {
  return (
    <div className="d-md-block d-none">
      <div className="top-menu container navigation d-flex justify-content-end">
        <ul className="nav">
          {primaryMenu.map((element, index) => (
            <React.Fragment key={element.id}>
              <li className="nav-item">
                <Link
                  className={`nav-link link-dark px-2 text-underline-hover ${
                    location.pathname === element.link ? "fw-bold" : ""
                  }`}
                  to={element.link}
                  target={element.target || ""}
                  onClick={(e) => {
                    if (location.pathname === element.link) {
                      e.preventDefault();
                      return;
                    }
                    if (element.onClick) {
                      element.onClick(e);
                    }
                  }}
                >
                  {element.name}
                </Link>
              </li>
              {index < primaryMenu.length - 1 && (
                <li className="nav-item mt7">|</li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>

      <div className="container d-flex pb-3">
        <div className="col">
          <Link
            to="/vehiclehistory/"
            onClick={(e) =>
              location.pathname === "/vehiclehistory/" && e.preventDefault()
            }
          >
            <img
              src={require("../../assets/images/autocheckLogo.png")}
              alt="AutoCheck by Experian"
              className="img-fluid"
            />
          </Link>
        </div>
        <div className="col mt-3">
          <SecondaryMenu onMenuClick={onMenuClick} />
        </div>
      </div>
    </div>
  );
}

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem("auth.currentUser");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const [primaryMenu, setPrimaryMenu] = useState([]);
  const TOKEN_EXPIRY_BUFFER_MS = 60_000;

  const isTokenExpired = () => {
    const token = localStorage.getItem("consumerSite_token");
    if (token) {
      try {
        const { exp } = jwtDecode(token);
        return Date.now() >= exp * 1000 - TOKEN_EXPIRY_BUFFER_MS;
      } catch {
        return true;
      }
    }
    return false;
  };

  const handleSignOut = useCallback(() => {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    // Clear cookies (if any)
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Clear Redux state
    dispatch(fetchLogin({ condition: "partnerSiteId", payload: 0 }));
    dispatch(clearView());
    setCurrentUser(null);

    // Navigate to login
    navigate("/vehiclehistory/login");
  }, [dispatch, navigate]);

  const generateMenu = useCallback(() => {
    const updatedMenu = [
      {
        id: 1,
        name: "Dealer Sign In",
        link: "https://www.autocheck.com/members/login.do",
        target: "_blank",
      },
      { id: 2, name: "Dispute", link: "/vehiclehistory/dispute" },
      { id: 3, name: "Contact Us", link: "/vehiclehistory/contact" },
    ];

    if (localStorage.getItem("consumerSite_token")) {
      updatedMenu.push(
        { id: 4, name: "My Reports", link: "/vehiclehistory/dashboard" },
        {
          id: 5,
          name: "Sign Out",
          link: "/vehiclehistory/login",
          onClick: handleSignOut,
        }
      );
    } else {
      updatedMenu.push({
        id: 4,
        name: "Consumer Sign In",
        link: "/vehiclehistory/login",
      });
    }
    return updatedMenu;
  }, [handleSignOut]);

  useEffect(() => {
    setPrimaryMenu(generateMenu());

    const handleStorageChange = (e) => {
      if (e.key === "consumerSite_token" || e.key === null) {
        setPrimaryMenu(generateMenu());
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const tokenCheckInterval = setInterval(() => {
      setPrimaryMenu(generateMenu());
    }, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(tokenCheckInterval);
    };
  }, [generateMenu]);

  useEffect(() => {
    // Only check token expiry if:
    // 1. A token exists in localStorage
    // 2. User is not already on the login page
    // 3. Token is actually expired
    const token = localStorage.getItem("consumerSite_token");

    if (
      token &&
      location.pathname !== "/vehiclehistory/login" &&
      isTokenExpired()
    ) {
      localStorage.clear();
      setCurrentUser(null);
      navigate("/vehiclehistory/login", {
        state: {
          sessionError:
            "Your previous session has expired. For security purposes, you must login again to continue.",
        },
      });
      dispatch(clearView());
    }
  }, [location.pathname, dispatch, navigate]);

  return (
    <header>
      <DesktopHeader
        primaryMenu={primaryMenu}
        location={location}
        onMenuClick={() => {}}
      />
      <MobileHeader
        onMenuClick={() => {}}
        primaryMenu={primaryMenu}
        handleSignOut={handleSignOut}
      />
    </header>
  );
}

export default Header;
